import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { ReadReceiptSubjectType } from '../../../frontend-libs/evlapp-types';
import { useAppSelector } from '../../hooks';
import { t } from '../../i18n';
import { getReadReceiptDetail } from '../../services/ReadReceiptService';
import { selectSecurity } from '../../store/securitySlice';
import { Icon } from '../Icon/Icon';
import { useModal } from '../Modal/useModal';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

import { ReadReceiptModal } from './ReadReceiptModal';

interface ReadReceiptBadgeProps {
  subjectType: ReadReceiptSubjectType;
  subjectId: string;
  /** Počty ze summary endpointu (seznamy) – badge pak nedělá vlastní fetch. */
  preloaded?: { readCount: number; totalCount: number };
  /** 'compact' = jen ikona s tooltipem (seznamy), skrytá dokud nikdo nezobrazil. */
  variant?: 'default' | 'compact';
  className?: string;
}

export const ReadReceiptBadge: React.FC<ReadReceiptBadgeProps> = (props) => {
  const { subjectType, subjectId, preloaded, variant = 'default' } = props;

  const isAdmin = useAppSelector(selectSecurity).ui.isAdmin;
  const [isShown, toggle] = useModal();
  const [fetched, setFetched] = useState<{ readCount: number; totalCount: number } | null>(null);

  const counts = preloaded ?? fetched;

  useEffect(() => {
    if (!isAdmin || preloaded !== undefined || fetched !== null) return;
    getReadReceiptDetail(subjectType, subjectId)
      .then((res) => setFetched({ readCount: res.readCount, totalCount: res.totalCount }))
      .catch(() => undefined);
  }, [isAdmin, preloaded, fetched, subjectType, subjectId]);

  if (!isAdmin || counts === null) return null;

  const badgeText = t('badge', 'ReadReceipt', { read: counts.readCount, total: counts.totalCount });
  const modal = <ReadReceiptModal subjectType={subjectType} subjectId={subjectId} isShown={isShown} onHide={toggle} />;

  if (variant === 'compact') {
    if (counts.readCount === 0) return null;

    return (
      <>
        <span className={cx('ReadReceiptBadge', 'link', props.className)} onClick={toggle}>
          <Tooltip text={badgeText} placement="top">
            <Icon name="checkmark-circle" color="Primary600" />
          </Tooltip>
        </span>
        {modal}
      </>
    );
  }

  return (
    <>
      <Text
        as="span"
        size="body-small"
        color="Primary600"
        className={cx('ReadReceiptBadge', 'link', props.className)}
        onClick={toggle}
        title={t('title', 'ReadReceipt')}
      >
        <Icon name="checkmark-circle" />
        {badgeText}
      </Text>
      {modal}
    </>
  );
};
